import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatError } from "@/auth/AuthContext";
import { ShoppingBag, Mail, Lock, User, AlertCircle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, acceptInvite } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/invites/check/${token}`)
      .then((r) => { setInvite(r.data); setName(r.data.name || ""); })
      .catch((e) => setError(formatError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (user === undefined) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Parola trebuie să aibă minim 6 caractere"); return; }
    setBusy(true);
    try {
      await acceptInvite(token, password, name);
      nav("/", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-2 inline-flex items-center gap-3">
            <div className="rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 p-2.5">
              <ShoppingBag className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-50">BragusApp</h1>
          </div>
          <p className="text-sm text-zinc-400">Ai fost invitat să te alături</p>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-6 text-center text-sm text-zinc-400">Se verifică invitația...</div>
        ) : error && !invite ? (
          <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-6 text-center">
            <AlertCircle className="mx-auto mb-2 h-8 w-8 text-rose-400" />
            <p className="text-sm text-rose-200">{error}</p>
            <button onClick={() => nav("/login")} className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-2 text-xs text-zinc-200">
              Înapoi la login
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="invite-form" className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-6">
            <div className="mb-5 flex items-center gap-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              <div>
                <p className="text-sm font-medium text-emerald-100">Invitație validă</p>
                <p className="text-xs text-emerald-300">{invite.email}</p>
              </div>
            </div>

            {error && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5" /><span>{error}</span>
              </div>
            )}

            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Numele tău</label>
            <div className="mb-3 flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
              <User className="h-4 w-4 text-zinc-500" />
              <input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)}
                className="flex-1 bg-transparent text-sm text-zinc-100 placeholder-zinc-600 outline-none" placeholder="Nume Prenume" />
            </div>

            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Setează parola (minim 6 caractere)</label>
            <div className="mb-5 flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2">
              <Lock className="h-4 w-4 text-zinc-500" />
              <input data-testid="invite-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-transparent text-sm text-zinc-100 placeholder-zinc-600 outline-none" placeholder="••••••••" />
            </div>

            <button data-testid="invite-submit" type="submit" disabled={busy}
              className="w-full rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2.5 text-sm font-medium text-white disabled:opacity-50">
              {busy ? "Se creează contul..." : "Creează contul și intră"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
